/* eslint-disable curly */
import {
  Excalidraw,
  MainMenu,
  CaptureUpdateAction,
  reconcileElements,
} from "@excalidraw/excalidraw";
import { languages, setLanguage, t } from "@excalidraw/excalidraw/i18n";
import { NOTEBOOK_PAGE_WIDTH, NOTEBOOK_WORKSPACE_BG } from "@excalidraw/common";
import { useCallback, useEffect, useRef, useState } from "react";

import type { RemoteExcalidrawElement } from "@excalidraw/excalidraw/data/reconcile";
import type {
  Collaborator,
  ExcalidrawImperativeAPI,
  ExcalidrawProps,
  SocketId,
} from "@excalidraw/excalidraw/types";
import type { Theme } from "@excalidraw/element/types";

import { getPreferredLanguage } from "../app-language/language-detector";

import {
  libraryApi,
  type NotebookPageStyle,
  type SharedScene,
} from "../data/libraryApi";
import { CollabSocket, type Peer } from "../data/collabSocket";
import { EMPTY_EXPORT, exportSceneToPdf } from "../data/exportPdf";

import "./SharePublicViewer.scss";

interface Props {
  token: string;
}

const GUEST_ID_KEY = "pw-guest-id";
const GUEST_NAME_KEY = "pw-guest-name";

const pdfIcon = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

const gridIcon = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="3" width="18" height="18" rx="1" />
    <line x1="9" y1="3" x2="9" y2="21" />
    <line x1="15" y1="3" x2="15" y2="21" />
    <line x1="3" y1="9" x2="21" y2="9" />
    <line x1="3" y1="15" x2="21" y2="15" />
  </svg>
);

const plusIcon = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const getGuestId = (): string => {
  let id = window.localStorage.getItem(GUEST_ID_KEY);
  if (!id) {
    id =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    window.localStorage.setItem(GUEST_ID_KEY, id);
  }
  return id;
};

export const SharePublicViewer = ({ token }: Props) => {
  const [data, setData] = useState<SharedScene | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [langCode, setLangCode] = useState<string>(() =>
    getPreferredLanguage(),
  );
  const [theme, setTheme] = useState<Theme>(() =>
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light",
  );

  const [guestName, setGuestName] = useState<string | null>(() =>
    window.localStorage.getItem(GUEST_NAME_KEY),
  );
  const [nameDraft, setNameDraft] = useState("");

  const [api, setApi] = useState<ExcalidrawImperativeAPI | null>(null);
  // Guards onChange from saving while we're applying a remote/initial scene
  // (prevents an echo loop between collaborators).
  const readyRef = useRef(false);
  // True while we have a local edit pending save — so an incoming pull doesn't
  // overwrite our just-made change (e.g. a notebook page-style switch).
  const pendingSaveRef = useRef(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const collabRef = useRef<CollabSocket | null>(null);
  const collaboratorsRef = useRef<Map<SocketId, Collaborator>>(new Map());
  const lastPointerSentRef = useRef(0);
  // True while mid-gesture — don't apply a remote scene then (it breaks the drag).
  const pointerDownRef = useRef(false);
  const apiRef = useRef<ExcalidrawImperativeAPI | null>(null);
  useEffect(() => {
    apiRef.current = api;
  }, [api]);

  // Standalone page: localize Excalidraw + our header explicitly.
  useEffect(() => {
    const lang = languages.find((l) => l.code === langCode);
    if (lang) {
      setLanguage(lang);
    }
  }, [langCode]);

  useEffect(() => {
    let cancelled = false;
    libraryApi
      .getShare(token)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const editable = !!(data && data.can_edit === 1 && data.canvas_id);
  const needsName = editable && !guestName;

  // For a shared notebook, force the gray workspace background + page surface so
  // the white sheet stands out (and center it). Mirrors the app's buildAppState.
  const buildViewerAppState = useCallback(
    (sceneAppState: unknown, centerWidth?: number): Record<string, unknown> => {
      const sa = (sceneAppState ?? {}) as Record<string, any>;
      const isNotebook =
        data?.canvas_type === "notebook" || sa.notebookMode === true;
      if (!isNotebook) {
        return sa;
      }
      const base: Record<string, unknown> = {
        ...sa,
        notebookMode: true,
        notebookPageStyle: sa.notebookPageStyle ?? data?.page_style ?? "ruled",
        notebookPageCount: Math.max(1, Math.floor(sa.notebookPageCount ?? 1)),
        notebookPageOpacity: sa.notebookPageOpacity ?? 100,
        viewBackgroundColor: NOTEBOOK_WORKSPACE_BG,
      };
      if (centerWidth) {
        const margin = 64;
        const zoomValue = Math.min(
          1,
          Math.max(0.12, (centerWidth - margin * 2) / NOTEBOOK_PAGE_WIDTH),
        );
        base.zoom = { value: zoomValue };
        base.scrollX = centerWidth / (2 * zoomValue) - NOTEBOOK_PAGE_WIDTH / 2;
        base.scrollY = 40 / zoomValue;
      }
      return base;
    },
    [data],
  );

  // The initial scene is loaded via Excalidraw's `initialData` (the proven path
  // the read-only viewer uses — reliable for the notebook surface). We only flip
  // readyRef on shortly AFTER mount so the editor's own init onChange doesn't
  // immediately re-save; real guest edits after that DO autosave.
  useEffect(() => {
    if (!editable || !api) {
      return;
    }
    const tid = setTimeout(() => {
      readyRef.current = true;
    }, 500);
    return () => clearTimeout(tid);
  }, [editable, api]);

  // Pull the latest scene from the server and merge it conflict-free with our
  // local edits (so neither side's work is lost).
  const pullAndMerge = useCallback(async () => {
    const a = apiRef.current;
    if (!a || pointerDownRef.current) {
      return; // skip while drawing — applying now would break the gesture
    }
    try {
      const fresh = await libraryApi.getShare(token);
      const merged = reconcileElements(
        a.getSceneElementsIncludingDeleted() as RemoteExcalidrawElement[],
        (fresh.scene.elements ?? []) as RemoteExcalidrawElement[],
        a.getAppState(),
      );
      const filesArr = Object.values(fresh.scene.files || {}) as Parameters<
        typeof a.addFiles
      >[0];
      if (filesArr.length > 0) {
        a.addFiles(filesArr);
      }
      // Converge on the server's notebook page settings (style/count/opacity) so
      // a peer's page-style change shows here too — but NOT if we have a local
      // edit pending (that would revert our own just-made change).
      const fsa = fresh.scene.appState as Record<string, unknown> | undefined;
      const appStatePatch: Record<string, unknown> = {};
      if (!pendingSaveRef.current && fsa?.notebookMode) {
        appStatePatch.notebookPageStyle = fsa.notebookPageStyle;
        appStatePatch.notebookPageCount = fsa.notebookPageCount;
        appStatePatch.notebookPageOpacity = fsa.notebookPageOpacity;
      }
      readyRef.current = false;
      a.updateScene({
        elements: merged as never,
        ...(Object.keys(appStatePatch).length
          ? { appState: appStatePatch as never }
          : {}),
        captureUpdate: CaptureUpdateAction.NEVER,
      });
      readyRef.current = true;
    } catch {
      /* ignore — next ping or edit will retry */
    }
  }, [token]);

  // Near-real-time refresh for the public viewer. A read-only share has NO
  // WebSocket, so without this it would only ever show the scene as it was when
  // the page loaded. Poll the share every 10s (plus immediately on focus / tab
  // becoming visible) so the viewer sees the owner's edits. The server's
  // GET /:token serves the LIVE canvas scene, so each pull reflects the latest.
  // Harmless on editable shares too: pullAndMerge reconciles conflict-free and
  // skips while drawing; the WS save-ping just makes it faster.
  useEffect(() => {
    if (!api) {
      return;
    }
    const interval = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        void pullAndMerge();
      }
    }, 10000);
    const onFocus = () => void pullAndMerge();
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        void pullAndMerge();
      }
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [api, pullAndMerge]);

  // --- Real-time collaboration (cursors + presence + save pings) ----------
  // Guard with readyRef so the onChange this triggers does NOT autosave: cursor/
  // presence updates aren't edits, and re-saving would spam the backend and
  // clobber a peer's appState (e.g. notebook style) with our stale copy.
  const flushCollaborators = useCallback(() => {
    const a = apiRef.current;
    if (a) {
      const wasReady = readyRef.current;
      readyRef.current = false;
      a.updateScene({ collaborators: new Map(collaboratorsRef.current) });
      readyRef.current = wasReady;
    }
  }, []);

  useEffect(() => {
    if (!editable || !guestName || !api) {
      return;
    }
    const socket = new CollabSocket();
    collabRef.current = socket;
    collaboratorsRef.current = new Map();

    socket.connect(
      { token, guestName, guestId: getGuestId() },
      {
        onPresence: (peers: Peer[], selfConnId) => {
          const next = new Map<SocketId, Collaborator>();
          for (const peer of peers) {
            if (peer.connId === selfConnId) continue;
            const key = peer.connId as SocketId;
            const prev = collaboratorsRef.current.get(key);
            next.set(key, { ...prev, id: peer.id, username: peer.username });
          }
          collaboratorsRef.current = next;
          flushCollaborators();
        },
        onPeerPointer: (p) => {
          if (p.connId === socket.getSelfConnId()) return;
          const key = p.connId as SocketId;
          const prev = collaboratorsRef.current.get(key);
          collaboratorsRef.current.set(key, {
            ...prev,
            id: p.id,
            username: p.username,
            pointer: { x: p.x, y: p.y, tool: "pointer" },
            button: p.button,
            selectedElementIds: p.selectedElementIds,
          });
          flushCollaborators();
        },
        onSceneSaved: () => {
          void pullAndMerge();
        },
        onAccessRevoked: () => {
          apiRef.current?.setToast({
            message: t("collab.accessRevoked"),
            duration: 4000,
          });
        },
        onNotebookSettings: (s) => {
          // A peer changed notebook page settings — apply instantly. Guarded so
          // it doesn't re-trigger our autosave (the sender already persisted it).
          const a = apiRef.current;
          if (!a) {
            return;
          }
          const patch: Record<string, unknown> = {};
          if (s.style !== undefined) {
            patch.notebookPageStyle = s.style;
          }
          if (typeof s.count === "number") {
            patch.notebookPageCount = s.count;
          }
          if (typeof s.opacity === "number") {
            patch.notebookPageOpacity = s.opacity;
          }
          if (Object.keys(patch).length === 0) {
            return;
          }
          const wasReady = readyRef.current;
          readyRef.current = false;
          a.updateScene({
            appState: patch as never,
            captureUpdate: CaptureUpdateAction.NEVER,
          });
          readyRef.current = wasReady;
        },
      },
    );
    return () => {
      socket.disconnect();
      collabRef.current = null;
      collaboratorsRef.current = new Map();
      const a = apiRef.current;
      if (a) {
        const wasReady = readyRef.current;
        readyRef.current = false;
        a.updateScene({ collaborators: new Map() });
        readyRef.current = wasReady;
      }
    };
  }, [editable, guestName, api, token, flushCollaborators, pullAndMerge]);

  const handlePointerUpdate: NonNullable<ExcalidrawProps["onPointerUpdate"]> =
    useCallback((payload) => {
      pointerDownRef.current = payload.button === "down";
      const socket = collabRef.current;
      if (!socket || collaboratorsRef.current.size === 0) {
        return;
      }
      const now = Date.now();
      if (now - lastPointerSentRef.current < 50) {
        return;
      }
      lastPointerSentRef.current = now;
      socket.sendPointer({
        x: payload.pointer.x,
        y: payload.pointer.y,
        button: payload.button,
        selectedElementIds: apiRef.current?.getAppState().selectedElementIds,
      });
    }, []);

  // Autosave (debounced) → reconcile with server → write back via the share.
  const onChange: NonNullable<ExcalidrawProps["onChange"]> = useCallback(
    (elements, appState, files) => {
      if (!readyRef.current) {
        return; // applying a remote/initial scene — not a user edit
      }
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
      }
      pendingSaveRef.current = true;
      const snapshot = {
        type: "excalidraw",
        version: 2,
        source: "self-hosted",
        elements: elements as unknown[],
        appState: appState.notebookMode
          ? {
              viewBackgroundColor: appState.viewBackgroundColor,
              notebookMode: true,
              notebookPageStyle: appState.notebookPageStyle,
              notebookPageCount: appState.notebookPageCount,
              notebookPageOpacity: appState.notebookPageOpacity,
            }
          : { viewBackgroundColor: appState.viewBackgroundColor },
        files: files as Record<string, unknown>,
      };
      saveTimer.current = setTimeout(() => {
        void (async () => {
          const a = apiRef.current;
          let toSave: unknown = snapshot;
          // Reconcile with the current server scene first so concurrent edits
          // from the owner/other guests aren't clobbered.
          try {
            const fresh = await libraryApi.getShare(token);
            if (a) {
              const merged = reconcileElements(
                elements as RemoteExcalidrawElement[],
                (fresh.scene.elements ?? []) as RemoteExcalidrawElement[],
                a.getAppState(),
              );
              toSave = { ...snapshot, elements: merged as unknown[] };
            }
          } catch {
            /* fall back to local snapshot */
          }
          await libraryApi.saveSharedScene(token, toSave).catch(() => {
            /* will retry on next edit */
          });
          pendingSaveRef.current = false;
        })();
      }, 1500);
    },
    [token],
  );

  const handleSaveAsPdf = useCallback(async () => {
    const a = apiRef.current;
    if (!a) {
      return;
    }
    try {
      await exportSceneToPdf(
        a.getSceneElements(),
        a.getAppState(),
        a.getFiles(),
        data?.name || "board",
      );
    } catch (e) {
      a.setToast({
        message:
          e instanceof Error && e.message === EMPTY_EXPORT
            ? t("fileExtras.nothingToExport")
            : t("fileExtras.pdfError"),
      });
    }
  }, [data]);

  const toggleGrid = useCallback(() => {
    const a = apiRef.current;
    if (!a) {
      return;
    }
    // View-only preference — suppress the autosave echo.
    readyRef.current = false;
    a.updateScene({
      appState: { gridModeEnabled: !a.getAppState().gridModeEnabled },
    });
    readyRef.current = true;
  }, []);

  // Notebook controls for the guest: change the page style / add a page. The
  // change persists via the autosave (which writes the notebook appState back to
  // the shared canvas), so the owner sees it too.
  const isNotebook =
    data?.canvas_type === "notebook" ||
    (data?.scene.appState as Record<string, unknown> | undefined)
      ?.notebookMode === true;

  const setNotebookStyle = useCallback((style: NotebookPageStyle) => {
    const a = apiRef.current;
    if (!a) {
      return;
    }
    a.updateScene({
      appState: { notebookPageStyle: style } as never,
      captureUpdate: CaptureUpdateAction.NEVER,
    });
    collabRef.current?.sendNotebookSettings({ style });
  }, []);

  const addNotebookPage = useCallback(() => {
    const a = apiRef.current;
    if (!a) {
      return;
    }
    const st = a.getAppState() as unknown as { notebookPageCount?: number };
    const next = Math.max(1, Math.floor(st.notebookPageCount ?? 1)) + 1;
    a.updateScene({
      appState: { notebookPageCount: next } as never,
      captureUpdate: CaptureUpdateAction.NEVER,
    });
    collabRef.current?.sendNotebookSettings({ count: next });
  }, []);

  const onLanguageChange = (code: string) => {
    setLangCode(code);
    try {
      window.localStorage.setItem("i18nextLng", code);
    } catch {
      /* ignore */
    }
  };

  const languageMenu = (
    <select
      className="dropdown-select dropdown-select__language"
      value={langCode}
      aria-label={t("buttons.selectLanguage")}
      style={{ width: "100%" }}
      onChange={(e) => onLanguageChange(e.target.value)}
    >
      {languages.map((lang) => (
        <option key={lang.code} value={lang.code}>
          {lang.label}
        </option>
      ))}
    </select>
  );

  if (error) {
    return (
      <div className="share-viewer share-viewer--error">
        <h1>{t("shareViewer.cannotLoad")}</h1>
        <p>{error}</p>
        <a href="/">{t("shareViewer.goToApp")}</a>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="share-viewer share-viewer--loading">
        {t("shareViewer.loading")}
      </div>
    );
  }

  const scene = data.scene;

  // Editable guest mode: ask for a display name first, then a full editor.
  if (editable) {
    return (
      <div className="share-viewer">
        <div className="share-viewer__header">
          <div className="share-viewer__heading">
            <span className="share-viewer__title">
              {data.name || t("shareViewer.untitled")}
            </span>
            {data.description && (
              <span className="share-viewer__description">
                {data.description}
              </span>
            )}
          </div>
          <span className="share-viewer__meta">{t("collab.guestEditing")}</span>
        </div>
        <div className="share-viewer__canvas">
          <Excalidraw
            onExcalidrawAPI={(a) => setApi(a)}
            langCode={langCode}
            theme={theme}
            onChange={onChange}
            onPointerUpdate={handlePointerUpdate}
            initialData={{
              elements: scene.elements as never,
              appState: buildViewerAppState(
                scene.appState,
                typeof window !== "undefined" ? window.innerWidth : undefined,
              ) as never,
              files: scene.files as never,
            }}
          >
            <MainMenu>
              <MainMenu.DefaultItems.Export />
              <MainMenu.DefaultItems.SaveAsImage />
              <MainMenu.Item
                icon={pdfIcon}
                onSelect={() => void handleSaveAsPdf()}
              >
                {t("fileExtras.saveAsPdf")}
              </MainMenu.Item>
              <MainMenu.Separator />
              <MainMenu.DefaultItems.ToggleTheme
                theme={theme}
                onSelect={(next) => setTheme(next as Theme)}
              />
              <MainMenu.ItemCustom>{languageMenu}</MainMenu.ItemCustom>
              {isNotebook ? (
                <>
                  <MainMenu.Item
                    icon={gridIcon}
                    onSelect={() => setNotebookStyle("ruled")}
                  >
                    {t("notebook.style.ruled")}
                  </MainMenu.Item>
                  <MainMenu.Item
                    icon={gridIcon}
                    onSelect={() => setNotebookStyle("grid")}
                  >
                    {t("notebook.style.grid")}
                  </MainMenu.Item>
                  <MainMenu.Item
                    icon={gridIcon}
                    onSelect={() => setNotebookStyle("dotted")}
                  >
                    {t("notebook.style.dotted")}
                  </MainMenu.Item>
                  <MainMenu.Item
                    icon={gridIcon}
                    onSelect={() => setNotebookStyle("blank")}
                  >
                    {t("notebook.style.blank")}
                  </MainMenu.Item>
                  <MainMenu.Item icon={plusIcon} onSelect={addNotebookPage}>
                    {t("notebook.addPage")}
                  </MainMenu.Item>
                </>
              ) : (
                <>
                  <MainMenu.DefaultItems.ChangeCanvasBackground />
                  <MainMenu.Item icon={gridIcon} onSelect={toggleGrid}>
                    {t("grid.title")}
                  </MainMenu.Item>
                </>
              )}
            </MainMenu>
          </Excalidraw>
        </div>

        {needsName && (
          <div className="share-viewer__name-overlay">
            <form
              className="share-viewer__name-card"
              onSubmit={(e) => {
                e.preventDefault();
                const name = nameDraft.trim() || t("collab.guestDefaultName");
                window.localStorage.setItem(GUEST_NAME_KEY, name);
                setGuestName(name);
              }}
            >
              <h2>{t("collab.guestNameTitle")}</h2>
              <p>{t("collab.guestNameHint")}</p>
              <input
                type="text"
                autoFocus
                maxLength={40}
                placeholder={t("collab.guestDefaultName")}
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
              />
              <button type="submit">{t("collab.guestEnter")}</button>
            </form>
          </div>
        )}
      </div>
    );
  }

  // Read-only viewer (default).
  return (
    <div className="share-viewer">
      <div className="share-viewer__header">
        <div className="share-viewer__heading">
          <span className="share-viewer__title">
            {data.name || t("shareViewer.untitled")}
          </span>
          {data.description && (
            <span className="share-viewer__description">
              {data.description}
            </span>
          )}
        </div>
        <span className="share-viewer__meta">
          {data.view_count === 1
            ? t("shareViewer.metaOne", { count: data.view_count })
            : t("shareViewer.metaOther", { count: data.view_count })}
        </span>
      </div>
      <div className="share-viewer__canvas">
        <Excalidraw
          viewModeEnabled
          zenModeEnabled
          langCode={langCode}
          theme={theme}
          initialData={{
            elements: scene.elements as never,
            appState: {
              ...buildViewerAppState(
                scene.appState,
                typeof window !== "undefined" ? window.innerWidth : undefined,
              ),
              viewModeEnabled: true,
            } as never,
            files: scene.files as never,
          }}
        >
          {/* Minimal menu: save/export + theme/language so viewers can adapt. */}
          <MainMenu>
            <MainMenu.DefaultItems.SaveAsImage />
            <MainMenu.DefaultItems.Export />
            <MainMenu.Separator />
            <MainMenu.DefaultItems.ToggleTheme
              theme={theme}
              onSelect={(next) => setTheme(next as Theme)}
            />
            <MainMenu.ItemCustom>{languageMenu}</MainMenu.ItemCustom>
          </MainMenu>
        </Excalidraw>
      </div>
    </div>
  );
};
